import _ from "lodash";
import { getColumnDefinitionId } from "./DataGridColumnDefinition";
export function applyPreferences(definitions, preferences) {
    const newDefinitions = _.cloneDeep(definitions);
    if (!preferences) {
        return newDefinitions;
    }
    newDefinitions.forEach((definition) => {
        const preference = preferences?.find((pref) => pref.columnId === getColumnDefinitionId(definition));
        //Apply isHidden
        if (definition.isNeverVisible) {
            definition.isVisible = false;
        }
        else {
            if (definition.fixed || !preference || preference.hidden === undefined) {
                definition.isVisible = definition.isVisible === undefined ? true : definition.isVisible;
            }
            else {
                definition.isVisible = !preference.hidden;
            }
        }
        //Apply width
        if (preference?.width) {
            definition.width = preference.width;
            definition.width = definition.maxWidth ? Math.min(preference.width, definition.maxWidth) : definition.width;
            definition.width = definition.minWidth ? Math.max(preference.width, definition.minWidth) : definition.width;
        }
        //Apply sort
        if (preference?.sort) {
            definition.sortOrder = preference.sort;
        }
    });
    //Apply column order
    const [fixedColumns, nonFixedColumns] = _.partition(newDefinitions, (def) => {
        return def.fixed === true;
    });
    const [unsortedDefintions, definitionsToSort] = _.partition(nonFixedColumns, (def) => {
        const preference = preferences?.find((pref) => pref.columnId === getColumnDefinitionId(def));
        return preference === null || preference === undefined || preference.sequence === null || preference.sequence === undefined || preference.sequence < 0;
    });
    definitionsToSort.sort((a, b) => {
        const preferenceA = preferences?.find((pref) => pref.columnId === getColumnDefinitionId(a));
        const preferenceB = preferences?.find((pref) => pref.columnId === getColumnDefinitionId(b));
        const preferenceASequence = preferenceA?.sequence || 0;
        const preferenceBSequence = preferenceB?.sequence || 0;
        if (preferenceASequence > preferenceBSequence) {
            return 1;
        }
        else if (preferenceASequence < preferenceBSequence) {
            return -1;
        }
        return 0;
    });
    return [...fixedColumns, ...unsortedDefintions, ...definitionsToSort];
}
/**
 *
 * @param columnOrder From grid state.  Typed as any
 * @param gridPreferences
 * @returns
 */
export function shouldUpdateColumnOrderPreference(columnOrder, gridPreferences) {
    const arePreferencesMissing = gridPreferences?.columnPreferences?.length !== columnOrder.length; //Check if there are preferences that don't have entries
    const columnHasChangedPosition = columnOrder.some((columnName, index) => {
        //Check if any of the columns have changed position
        return gridPreferences?.columnPreferences[index]?.columnId !== columnName;
    });
    return arePreferencesMissing || columnHasChangedPosition;
}
export function updateColumnOrderPreference(gridPreferences, updateGridPreference, columnOrder) {
    const newPreferences = _.cloneDeep(gridPreferences);
    let orderHasChanged = false;
    columnOrder.forEach((columnId, index) => {
        const [preference, wasCreated] = getColumnPreferenceByColumnId(newPreferences.columnPreferences, columnId);
        if (preference.sequence !== index + 1) {
            orderHasChanged = true;
        }
        preference.sequence = index + 1;
        if (wasCreated) {
            newPreferences.columnPreferences.push(preference);
        }
    });
    if (orderHasChanged) {
        updateGridPreference(newPreferences);
    }
    return newPreferences;
}
export function shouldUpdateColumnWidthPreference(columnResizing) {
    return !columnResizing.isResizingColumn && columnResizing.headerIdWidths && columnResizing.headerIdWidths.length > 0;
}
export function updateColumnWidthPreference(gridPreferences, updateGridPreference, stateColumnWidths) {
    const newPreferences = _.cloneDeep(gridPreferences);
    const columnWidthKeys = Object.keys(stateColumnWidths);
    let widthHasUpdated = false;
    columnWidthKeys.forEach((key) => {
        const [preference, wasCreated] = getColumnPreferenceByColumnId(newPreferences.columnPreferences, key);
        if (preference.width !== stateColumnWidths[key]) {
            preference.width = stateColumnWidths[key];
            widthHasUpdated = true;
        }
        if (wasCreated) {
            newPreferences.columnPreferences.push(preference);
        }
    });
    if (widthHasUpdated) {
        updateGridPreference(newPreferences);
    }
    return newPreferences;
}
/**
 * This is called when the view by refiners in a grid change. It will update the view by preferences in the grid preferences.
 * @param gridPreferences The existing grid preferences before the update
 * @param updateGridPreference a function that persists the grid prefererences to local storage
 * @param viewByRefiners the view by refiners to apply to the view by preferences in grid preferences
 * @returns the updated grid preferences
 */
export function updateViewByPreference(gridPreferences, updateGridPreference, viewByRefiners) {
    const newPreferences = _.cloneDeep(gridPreferences);
    if (!_.isEqual(newPreferences.viewByPreferences, viewByRefiners)) {
        newPreferences.viewByPreferences = _.cloneDeep(viewByRefiners);
        updateGridPreference(newPreferences);
    }
    return newPreferences;
}
/**
 * This is called when refiners in a grid change. It will update the refiner preferences in the grid preferences.
 * @param gridPreferences The existing grid preferences before the update
 * @param updateGridPreference a function that persists the updated grid prefererences to local storage
 * @param refiners the refiners to apply to the refiner preferences in grid preferences
 * @returns the updated grid preferences
 */
export function updateRefinersPreference(gridPreferences, updateGridPreference, refiners) {
    const newPreferences = _.cloneDeep(gridPreferences);
    if (!_.isEqual(newPreferences.refinerPreferences, refiners)) {
        newPreferences.refinerPreferences = _.cloneDeep(refiners);
        updateGridPreference(newPreferences);
    }
    return newPreferences;
}
export function shouldUpdateSortByPreference() {
    return true;
}
export function updateSortByPreference(gridPreferences, updateGridPreference, sortBy) {
    const newPreferences = _.cloneDeep(gridPreferences);
    let sortByHasChanged = false;
    //Remove sortBy when sorting rule removed
    newPreferences?.columnPreferences
        .filter((pref) => {
        const sortBySetting = sortBy.find((sort) => sort.id === pref.columnId);
        return !sortBySetting && pref.sort !== null && pref.sort !== undefined;
    })
        .forEach((pref) => {
        pref.sort = null;
        sortByHasChanged = true;
    });
    sortBy.forEach((sortByRule) => {
        const [preference, wasCreated] = getColumnPreferenceByColumnId(newPreferences.columnPreferences, sortByRule.id);
        if (wasCreated) {
            preference.sort = sortByRule.desc ? "desc" : "asc";
            newPreferences.columnPreferences.push(preference);
            sortByHasChanged = true;
        }
        else if (sortByRule.desc === true) {
            if (preference.sort !== "desc") {
                preference.sort = "desc";
                sortByHasChanged = true;
            }
        }
        else {
            if (preference.sort !== "asc") {
                preference.sort = "asc";
                sortByHasChanged = true;
            }
        }
    });
    if (sortByHasChanged) {
        updateGridPreference(newPreferences);
    }
    return newPreferences;
}
export function getPreferencesFromColumnDefinitions(gridId, columnDefinitions) {
    const columnPreferences = columnDefinitions.map((cd) => {
        return {
            columnId: getColumnDefinitionId(cd),
            hidden: cd.isVisible === undefined ? false : !cd.isVisible
        };
    });
    return {
        id: gridId,
        columnPreferences: columnPreferences
    };
}
export function getColumnPreferenceByColumnId(columnPreferences, id) {
    const preference = columnPreferences.find((colpref) => colpref.columnId === id);
    if (!preference) {
        return [{ columnId: id }, true];
    }
    return [preference, false];
}
/**
 * Read from grid preferences to set the initial sort order on each column in the grid
 * @param preferences Optional.  If not present then all columns are set to not sort by default
 * @returns An array of sorting rules, one for each column with an applied sort
 */
export function getInitialSortBySettings(preferences) {
    if (!preferences || preferences.length === 0) {
        return [];
    }
    return preferences
        .filter((pref) => pref.sort !== undefined && pref.sort !== null)
        .map((pref) => {
        let desc = undefined;
        if (pref.sort === "asc") {
            desc = false;
        }
        else if (pref.sort === "desc") {
            desc = true;
        }
        return {
            id: pref.columnId,
            desc: desc
        };
    });
}
